import { css } from 'aphrodite';
import * as React from 'react';
import type { Category, Scenario, SkillArea } from '../../../voice-agent/types/scenarios';
import { Button } from '../../../../web/components-v2/Button';
import { SkillAreaRow } from './SkillAreaRow';
import { styleSheet } from './styles';

/**
 * Grouped skill area with its scenarios
 */
interface GroupedSkillArea {
	skillArea: SkillArea;
	scenarios: Scenario[];
}

/**
 * Props for the CategoryRow component
 */
export interface CategoryRowProps {
	/** The category to display */
	category: Category;
	/** Skill areas belonging to this category with their scenarios */
	skillAreas: GroupedSkillArea[];
	/** Whether this category row is expanded */
	isExpanded: boolean;
	/** Callback when the category expansion is toggled */
	onToggle: () => void;
	/** Callback when the edit button is clicked */
	onEdit: () => void;
	/** Callback when the delete button is clicked */
	onDelete: () => void;
	/** Callback when the add skill area button is clicked */
	onAddSkillArea: () => void;
	/** Callback when a skill area edit button is clicked */
	onEditSkillArea: (skillArea: SkillArea) => void;
	/** Callback when a skill area delete button is clicked */
	onDeleteSkillArea: (skillArea: SkillArea) => void;
	/** Callback when add scenario button is clicked for a skill area */
	onAddScenario: (skillAreaId: string) => void;
	/** Callback when a scenario edit button is clicked */
	onEditScenario: (scenario: Scenario) => void;
	/** Callback when a scenario delete button is clicked */
	onDeleteScenario: (scenario: Scenario) => void;
	/** Set of expanded skill area IDs */
	expandedSkillAreas: Set<string>;
	/** Callback when a skill area expansion is toggled */
	onToggleSkillArea: (id: string) => void;
}

/**
 * CategoryRow component
 *
 * Renders a single category row with its nested skill areas and scenarios.
 * The category row displays the category name, role badge, status badge,
 * count of skill areas, and action buttons.
 *
 * When expanded, it shows nested skill area rows which can also be expanded
 * to show their scenarios.
 */
export const CategoryRow: React.FC<CategoryRowProps> = ({
	category,
	skillAreas,
	isExpanded,
	onToggle,
	onEdit,
	onDelete,
	onAddSkillArea,
	onEditSkillArea,
	onDeleteSkillArea,
	onAddScenario,
	onEditScenario,
	onDeleteScenario,
	expandedSkillAreas,
	onToggleSkillArea,
}) => {
	/**
	 * Handle keyboard navigation for the category row
	 */
	const handleKeyDown = React.useCallback(
		(event: React.KeyboardEvent) => {
			if (event.key === 'Enter' || event.key === ' ') {
				event.preventDefault();
				onToggle();
			}
		},
		[onToggle]
	);

	return (
		<div>
			{/* Category row */}
			<div className={css(styleSheet.row, styleSheet.categoryRow)}>
				<div
					className={css(styleSheet.chevronContainer)}
					onClick={onToggle}
					onKeyDown={handleKeyDown}
					role='button'
					tabIndex={0}
					aria-expanded={isExpanded}
					aria-label={`${isExpanded ? 'Collapse' : 'Expand'} ${category.name}`}
				>
					<svg
						className={css(styleSheet.chevron, isExpanded && styleSheet.chevronExpanded)}
						viewBox='0 0 24 24'
						aria-hidden='true'
					>
						<path d='M8.59 16.59L13.17 12 8.59 7.41 10 6l6 6-6 6-1.41-1.41z' />
					</svg>
				</div>
				<div className={css(styleSheet.rowContent)}>
					<div className={css(styleSheet.rowName)}>{category.name}</div>
					{category.description && <div className={css(styleSheet.rowDescription)}>{category.description}</div>}
				</div>
				<div className={css(styleSheet.roleBadge)}>{category.role}</div>
				<div
					className={css(styleSheet.statusBadge, category.active ? styleSheet.statusActive : styleSheet.statusInactive)}
				>
					{category.active ? 'Active' : 'Inactive'}
				</div>
				<div className={css(styleSheet.countBadge)}>{skillAreas.length} skill areas</div>
				<div className={css(styleSheet.actions)}>
					<Button variant='secondary' size='sm' onClick={onAddSkillArea}>
						+ Skill Area
					</Button>
					<Button variant='secondary' size='sm' onClick={onEdit}>
						Edit
					</Button>
					<Button variant='destructive-secondary' size='sm' onClick={onDelete}>
						Delete
					</Button>
				</div>
			</div>

			{/* Nested skill areas (if expanded) */}
			{isExpanded && (
				<div className={css(styleSheet.nestedContent)}>
					{skillAreas.map(({ skillArea, scenarios }) => (
						<SkillAreaRow
							key={skillArea.id}
							skillArea={skillArea}
							scenarios={scenarios}
							isExpanded={expandedSkillAreas.has(skillArea.id)}
							onToggle={() => onToggleSkillArea(skillArea.id)}
							onEdit={() => onEditSkillArea(skillArea)}
							onDelete={() => onDeleteSkillArea(skillArea)}
							onAddScenario={() => onAddScenario(skillArea.id)}
							onEditScenario={onEditScenario}
							onDeleteScenario={onDeleteScenario}
						/>
					))}
					{skillAreas.length === 0 && (
						<div className={css(styleSheet.row, styleSheet.skillAreaRow)}>
							<div className={css(styleSheet.chevronPlaceholder)} />
							<div className={css(styleSheet.rowContent)}>
								<div className={css(styleSheet.rowDescription)}>No skill areas yet</div>
							</div>
						</div>
					)}
				</div>
			)}
		</div>
	);
};
